/**
 * ConfirmDialog Component
 * Confirmation dialog for destructive actions
 */

import type { ReactNode } from 'react';
import { Modal } from './Modal';

interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: ReactNode;
  confirmText?: string;
  cancelText?: string;
  isDestructive?: boolean;
  isLoading?: boolean;
}

export function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  isDestructive = false,
  isLoading = false,
}: ConfirmDialogProps) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} width="small">
      <div className="mb-6 text-sm text-foreground leading-relaxed">{message}</div>
      <div className="flex justify-end gap-3">
        <button
          onClick={onClose}
          className="px-5 py-2.5 text-sm font-medium text-muted-foreground bg-card border border-border rounded-md cursor-pointer hover:bg-accent disabled:opacity-60 disabled:cursor-not-allowed min-h-[44px]"
          disabled={isLoading}
          data-testid="confirm-dialog-cancel"
        >
          {cancelText}
        </button>
        <button
          onClick={onConfirm}
          className={`px-5 py-2.5 text-sm font-medium border-none rounded-md cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed min-h-[44px] ${
            isDestructive
              ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
              : 'bg-primary text-primary-foreground hover:bg-primary/90'
          }`}
          disabled={isLoading}
          data-testid="confirm-dialog-confirm"
        >
          {isLoading ? 'Processing...' : confirmText}
        </button>
      </div>
    </Modal>
  );
}

export default ConfirmDialog;
